import { useFormikContext } from "formik";
import { IVehicleInfo, IVehicleTransfer } from "@/types/vehicleTransfer.type";
import { HiTrash } from "react-icons/hi2";
import * as Yup from "yup";
import { readFileAsBase64 } from "@/pages/home/VehicleSituation";
import { convertedImageToTextPost } from "@/common/services/vehicles.api";
import { RiUpload2Line } from "react-icons/ri";
import { useState } from "react";
import { useToastContext } from "@/common/components/ui/toast/toast.context";

type FormEditVehicleProps = {
  transfer: IVehicleTransfer;
  isReadOnly?: boolean;
};

const plateSchema = Yup.string()
  .transform((value) => value.toUpperCase().trim())
  .required("A placa é obrigatória.");

function FormEditVehicle({
  transfer,
  isReadOnly = false,
}: FormEditVehicleProps) {
  const { values, setFieldValue } = useFormikContext<{
    vehicles: IVehicleInfo[];
    newVehicleLicensePlate: string;
    newVehicleContent: string;
  }>();
  const { notifySuccess, notifyError } = useToastContext();
  const [isLoading, setIsLoading] = useState(false);

  const handleAddVehicle = (plate: string) => {
    const newPlate = plate;

    try {
      plateSchema.validateSync(newPlate);

      const formattedPlate = newPlate.toUpperCase().trim();

      if (values.vehicles.some((v) => v.licensePlate === formattedPlate)) {
        alert("Esta placa já foi adicionada.");
        return;
      }

      const newVehicle: IVehicleInfo = {
        licensePlate: formattedPlate,
        companyId: transfer.companyOriginId,
      };

      setFieldValue("vehicles", [...values.vehicles, newVehicle]);
      setFieldValue("newVehicleLicensePlate", "");
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        alert(error.message);
      }
    }
  };

  const handleRemoveVehicle = (indexToRemove: number) => {
    const updatedVehicles = values.vehicles.filter(
      (_, index) => index !== indexToRemove
    );
    setFieldValue("vehicles", updatedVehicles);
  };

  const handleFileInput = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIsLoading(true);

    const file = event.target.files?.[0];
    if (!file) {
      notifyError("Selecione um arquivo!");
      return;
    }

    try {
      const base64Data = await readFileAsBase64(file);
      if (!base64Data) {
        notifyError("Erro ao converter arquivo para base64");
        return;
      }

      const licensePlateResp = await convertedImageToTextPost(base64Data);
      if (!licensePlateResp.ok) {
        notifyError(
          licensePlateResp.message || "Falha ao extrair texto da imagem"
        );
        return;
      }

      const plate = licensePlateResp.data;
      handleAddVehicle(plate);
      notifySuccess("Placa do veículo extraída com sucesso");
    } catch (error) {
      notifyError("Ocorreu um erro ao processar o arquivo");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      {!isReadOnly && (
        <div className="flex flex-col gap-4 p-4 border rounded-md items-start">
          <div className="w-full max-w-[780px] mx-auto p-4">
            <button
              type="button"
              onClick={() => document.getElementById("fileInput")?.click()}
              disabled={isLoading}
              className={`
                      flex flex-col items-center justify-center w-full h-32
                      bg-gradient-to-r from-locavibe to-red-500 
                      text-white rounded-2xl shadow-md 
                      transition-all duration-200
                      focus:outline-none focus:ring-2 focus:ring-blue-300 
                      disabled:opacity-60 disabled:cursor-not-allowed
                    `}
            >
              {isLoading ? (
                <>
                  <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin mb-2" />
                  <span className="text-sm font-medium">Adicionando...</span>
                </>
              ) : (
                <>
                  <span className="text-4xl mb-2">
                    <RiUpload2Line />
                  </span>
                  <span className="text-sm font-medium">Adicionar Veículo</span>
                </>
              )}
            </button>
            <input
              id="fileInput"
              type="file"
              onChange={handleFileInput}
              accept="image/*"
              capture="environment"
              className="hidden"
            />
          </div>
        </div>
      )}
      <hr className="my-6" />

      <h3 className="text-lg font-semibold mb-2">
        Veículos na Transferência ({values.vehicles.length})
      </h3>
      <div className="space-y-2 max-h-60 overflow-y-auto">
        {values.vehicles.length === 0 && (
          <p className="text-gray-500">Nenhum veículo adicionado.</p>
        )}
        {values.vehicles.map((vehicle, index) => (
          <div
            key={index}
            className="flex items-center justify-between p-2 border rounded-md bg-gray-50"
          >
            <span>
              Placa:{" "}
              <span className="font-mono bg-gray-200 px-2 py-1 rounded">
                {vehicle.licensePlate}
              </span>
            </span>
            {!isReadOnly && (
              <button
                type="button"
                onClick={() => handleRemoveVehicle(index)}
                className="text-red-500 hover:text-red-700 p-1"
              >
                <HiTrash size={20} />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default FormEditVehicle;
